import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import client from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "../components/ui/popover";
import {
  Wallet, TrendingUp, TrendingDown, Receipt, Plus, Trash2, ArrowLeft, Calendar,
  GraduationCap, DollarSign, StickyNote, Coins, AlertTriangle, Download,
  Building2, Gem, Settings2, HelpCircle, Pencil, Check, BarChart3, BedDouble,
} from "lucide-react";
import { toast } from "sonner";
import PnlNotesDialog from "../components/PnlNotesDialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";

import {
  SOURCE_LABELS, TABS, MONTHS, COST_TYPES, COST_TYPE_LABELS, COST_TYPE_STYLES,
  ALL_UNITS, MAIN_ID, UNIT_KIND_LABELS, gbp, mad, eur, iso, AmountInput,
} from "./pnlShared";
import { tr } from "../i18n/LangContext";

export default function PnL() {
  const { stopActing } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("all");
  const [rates, setRates] = useState({ gbp_to_mad: 12.3, eur_to_mad: 10.5, month: "" });
  const pendingDeletes = useRef({});
  const [notesRow, setNotesRow] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null); // { kind, row }
  const [unit, setUnit] = useState(ALL_UNITS);
  const [units, setUnits] = useState([]);
  const [manageOpen, setManageOpen] = useState(false);
  const [newUnitName, setNewUnitName] = useState("");
  const [newUnitKind, setNewUnitKind] = useState("property");
  const [renaming, setRenaming] = useState(null); // { id, name }
  const [monthly, setMonthly] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const now = new Date();
  const [period, setPeriod] = useState(() => {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { label: `${tr("This month")} (${tr(MONTHS[now.getMonth()])} ${now.getFullYear()})`, start: iso(start), end: iso(end) };
  });

  const load = useCallback(async () => {
    try {
      const params = { unit };
      if (period.start) params.start = period.start;
      if (period.end) params.end = period.end;
      const r = await client.get("/pnl", { params });
      setData(r.data);
      if (r.data.units) setUnits(r.data.units);
      if (r.data.rates) setRates(r.data.rates);
    } catch (e) { toast.error(tr("Couldn't load P&L"), { description: e?.response?.data?.detail }); }
  }, [period, unit]);
  useEffect(() => { load(); }, [load]);

  const loadMonthly = useCallback(async () => {
    try {
      const r = await client.get("/pnl/monthly", { params: { year, unit } });
      setMonthly(r.data);
    } catch (e) { console.error("P&L monthly breakdown failed", e); }
  }, [year, unit]);
  useEffect(() => { loadMonthly(); }, [loadMonthly]);

  const unitName = (id) => units.find((u) => u.id === id)?.name || "Moroccan Brothers";
  const unitOf = (id) => units.find((u) => u.id === (id || MAIN_ID)) || units.find((u) => u.id === MAIN_ID);
  const unitKind = (id) => unitOf(id)?.kind || "business";
  const isMadRow = (id) => unitOf(id)?.currency === "MAD";
  // Property ventures get utility-style categories and booking channels; the mentorship side keeps its own.
  const optionsFor = (map, id, fallback, current) => {
    const list = (map || {})[unitKind(id)] || fallback;
    return current && !list.includes(current) ? [...list, current] : list;
  };
  const catsFor = (row) => optionsFor(data?.categories_by_kind, row.unit_id, categories, row.category);
  const sourcesFor = (row) => optionsFor(data?.sources_by_kind, row.unit_id, sources, row.source);
  const activeUnitLabel = unit === ALL_UNITS ? tr("All Businesses") : unitName(unit);
  // New rows land in the business you're looking at; "All" defaults to the main venture.
  const defaultUnitId = unit === ALL_UNITS ? (data?.main_unit_id || "unit_main") : unit;

  const addUnit = async () => {
    const name = newUnitName.trim();
    if (!name) return;
    try {
      const r = await client.post("/pnl/units", { name, kind: newUnitKind, currency: newUnitKind === "property" ? "MAD" : "GBP" });
      setUnits((u) => [...u, r.data]);
      setNewUnitName("");
      toast.success(`${name} added`);
    } catch (e) { toast.error(tr("Couldn't add business"), { description: e?.response?.data?.detail }); }
  };
  const saveRename = async () => {
    if (!renaming?.name.trim()) return;
    try {
      await client.put(`/pnl/units/${renaming.id}`, { name: renaming.name.trim() });
      setUnits((u) => u.map((x) => (x.id === renaming.id ? { ...x, name: renaming.name.trim() } : x)));
      setRenaming(null);
      load();
    } catch (e) { toast.error(tr("Couldn't rename"), { description: e?.response?.data?.detail }); }
  };
  const removeUnit = async (u) => {
    try {
      await client.delete(`/pnl/units/${u.id}`);
      setUnits((list) => list.filter((x) => x.id !== u.id));
      if (unit === u.id) setUnit(ALL_UNITS);
      toast.success(`${u.name} removed — its rows moved to the main business`);
      load(); loadMonthly();
    } catch (e) { toast.error(tr("Couldn't remove"), { description: e?.response?.data?.detail }); }
  };

  const back = () => { stopActing(); window.location.assign("/admin"); };

  // £ -> MAD -> EUR (computed fallback); per-row manual overrides win.
  const toMad = (g) => Number(g || 0) * Number(rates.gbp_to_mad || 0);
  const toEur = (g) => rates.eur_to_mad ? toMad(g) / Number(rates.eur_to_mad) : 0;
  const rowMad = (r) => (r.amountMad != null ? Number(r.amountMad) : toMad(r.amount));
  const rowEur = (r) => (r.amountEur != null ? Number(r.amountEur) : toEur(r.amount));
  const fromMad = (m) => (rates.gbp_to_mad ? Number(m || 0) / Number(rates.gbp_to_mad) : 0);

  const saveRates = async (next) => {
    const merged = { ...rates, ...next };
    setRates(merged);
    try {
      await client.put("/pnl/rates", {
        month: rates.month || (period.start ? period.start.slice(0, 7) : iso(new Date()).slice(0, 7)),
        gbp_to_mad: Number(merged.gbp_to_mad) || 0, eur_to_mad: Number(merged.eur_to_mad) || 0,
      });
      toast.success(tr("Rates saved for this period"));
      load(); loadMonthly();
    } catch (e) { toast.error(tr("Couldn't save rates")); }
  };

  const activeTab = TABS.find((t) => t[0] === tab) || TABS[0];
  const revenue = useMemo(() => {
    const rev = data?.revenue || [];
    const filt = activeTab[2];
    const list = filt ? rev.filter((r) => filt.includes(r.source)) : rev;
    // Latest transactions first; earliest of the period at the bottom.
    return [...list].sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  }, [data, activeTab]);
  const expenses = useMemo(() => {
    return [...(data?.expenses || [])].sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  }, [data]);
  const totalRev = revenue.reduce((s, r) => s + Number(r.amount || 0), 0);  const totalRevMad = revenue.reduce((s, r) => s + rowMad(r), 0);
  const totalRevEur = revenue.reduce((s, r) => s + rowEur(r), 0);
  const totalExp = expenses.reduce((s, e) => s + Number(e.amount || 0), 0);
  const net = totalRev - totalExp;
  // Gross − operating − luxury − unclassified = net profit.
  const costBuckets = useMemo(() => {
    const out = { operating: 0, luxury: 0, unclassified: 0 };
    expenses.forEach((e) => {
      const t = COST_TYPES.includes(e.cost_type) ? e.cost_type : "unclassified";
      out[t] += Number(e.amount || 0);
    });
    return out;
  }, [expenses]);
  const unclassifiedCount = expenses.filter((e) => !e.cost_type || e.cost_type === "unclassified").length;
  // A property venture reads in dirhams (that's how its bills land); the group view stays in £ so ventures add up.
  const scopeMad = unit !== ALL_UNITS && isMadRow(unit);
  const money = (g) => (scopeMad ? mad(toMad(g)) : gbp(g));
  const isProp = !!monthly?.is_property;
  // Once you've picked a venture, rows stay pinned to it — no accidental re-tagging.
  const propScope = unit !== ALL_UNITS && unitKind(unit) === "property";
  const lockedUnitCell = (row, testId) => (
    <td className="pr-3">
      <span data-testid={testId} className="inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-lg border border-white/[0.08] bg-white/[0.02] px-2.5 text-xs text-muted-foreground">
        <Building2 className="h-3.5 w-3.5 shrink-0 text-brand-green" />{unitName(row.unit_id)}
      </span>
    </td>
  );
  const stayNights = (r) => {
    if (!r.check_in || !r.check_out) return 0;
    const d = (new Date(r.check_out) - new Date(r.check_in)) / 86400000;
    return d > 0 ? Math.round(d) : 0;
  };
  const revNights = revenue.reduce((s, r) => s + stayNights(r), 0);
  // Nights booked are entered once per month per property; occupancy = nights / nights in the month.
  const saveNights = async (month, nights) => {
    setMonthly((mm) => (mm ? { ...mm, months: mm.months.map((x) => (x.month === month ? { ...x, nights } : x)) } : mm));
    try { await client.put("/pnl/occupancy", { unit_id: unit, month, nights }); } catch (e) { toast.error(tr("Couldn't save nights")); }
    loadMonthly();
  };

  // Shared CSV helpers.
  const csvEsc = (c) => { const s = String(c ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const downloadCsv = (filename, rows, rowCount) => {
    const csv = rows.map((row) => row.map(csvEsc).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    if (rowCount != null) toast.success(`Exported ${rowCount} row${rowCount === 1 ? "" : "s"}`);
  };
  // Preamble captures the FX rates + period so historical exports stay reproducible.
  const fxPreamble = (title) => [
    [title],
    ["Business", activeUnitLabel],
    ["Period", period.start || "all", "to", period.end || ""],
    ["FX rates", `£1 = ${rates.gbp_to_mad} DH`, `€1 = ${rates.eur_to_mad} DH`],
    [],
  ];

  const exportCsv = () => {
    if (!revenue.length) { toast.info(tr("No revenue to export in this tab/period.")); return; }
    const stayCols = propScope ? ["Check-in", "Check-out", "Nights"] : [];
    const closerCols = propScope ? [] : ["Closer"];
    const header = ["Date", "Name", "Business", ...stayCols, "Source", ...closerCols, "Amount (GBP)", "MAD", "EUR"];
    const body = revenue.map((r) => [
      r.date || "", r.name || "", unitName(r.unit_id),
      ...(propScope ? [r.check_in || "", r.check_out || "", stayNights(r) || ""] : []),
      SOURCE_LABELS[r.source] || r.source || "",
      ...(propScope ? [] : [r.closer || ""]),
      Number(r.amount || 0), Math.round(rowMad(r)), Math.round(rowEur(r) * 100) / 100,
    ]);
    downloadCsv(`pnl-revenue-${tab}-${period.start || "all"}.csv`,
      [...fxPreamble(`P&L Revenue Export — ${activeTab[1]}`), header, ...body], body.length);
  };

  const exportExpensesCsv = () => {
    if (!expenses.length) { toast.info(tr("No expenses to export in this period.")); return; }
    const header = ["Date", "Business", "Category", "Cost type", "Description", "Amount (GBP)", "Amount (DH)", "Note"];
    const body = expenses.map((e) => [
      e.date || "", unitName(e.unit_id), e.category || "",
      COST_TYPE_LABELS[e.cost_type] || "Unclassified", e.description || "", Number(e.amount || 0),
      Math.round(e.amountMad != null ? e.amountMad : toMad(e.amount)), e.note || "",
    ]);
    downloadCsv(`pnl-expenses-${period.start || "all"}.csv`,
      [...fxPreamble("P&L Expenses Export"), header, ...body], body.length);
  };

  // Full-period snapshot: summary + all revenue + all expenses in one file.
  const exportNetCsv = () => {
    const allRev = data?.revenue || [];
    const allExp = data?.expenses || [];
    if (!allRev.length && !allExp.length) { toast.info(tr("Nothing to export in this period.")); return; }
    const rev = Number(allRev.reduce((s, r) => s + Number(r.amount || 0), 0).toFixed(2));
    const revMad = Math.round(allRev.reduce((s, r) => s + rowMad(r), 0));
    const revEur = Math.round(allRev.reduce((s, r) => s + rowEur(r), 0) * 100) / 100;
    const b = { operating: 0, luxury: 0, unclassified: 0 };
    allExp.forEach((e) => { b[COST_TYPES.includes(e.cost_type) ? e.cost_type : "unclassified"] += Number(e.amount || 0); });
    const exp = Number(allExp.reduce((s, e) => s + Number(e.amount || 0), 0).toFixed(2));
    const rows = [
      ...fxPreamble("P&L Summary Export"),
      ["SUMMARY", "GBP", "MAD", "EUR"],
      ["Gross Revenue", rev, revMad, revEur],
      ["Net Operating Costs", Number(b.operating.toFixed(2)), "", ""],
      ["Luxury Costs", Number(b.luxury.toFixed(2)), "", ""],
      ["Unclassified Costs", Number(b.unclassified.toFixed(2)), "", ""],
      ["Total Expenses", exp, "", ""],
      ["Net Profit", Number((rev - exp).toFixed(2)), "", ""],
      [],
      ["REVENUE"],
      ["Date", "Name", "Business", "Source", "Closer", "Amount (GBP)", "MAD", "EUR"],
      ...allRev.map((r) => [
        r.date || "", r.name || "", unitName(r.unit_id), SOURCE_LABELS[r.source] || r.source || "",
        r.closer || "", Number(r.amount || 0), Math.round(rowMad(r)), Math.round(rowEur(r) * 100) / 100,
      ]),
      [],
      ["EXPENSES"],
      ["Date", "Business", "Category", "Cost type", "Description", "Amount (GBP)", "Note"],
      ...allExp.map((e) => [e.date || "", unitName(e.unit_id), e.category || "",
        COST_TYPE_LABELS[e.cost_type] || "Unclassified", e.description || "", Number(e.amount || 0), e.note || ""]),
    ];
    downloadCsv(`pnl-summary-${period.start || "all"}.csv`, rows, allRev.length + allExp.length);
  };

  // Jan–Dec gross / operating / luxury / net for the selected business.
  const exportMonthlyCsv = () => {
    if (!monthly?.months?.length) { toast.info(tr("Nothing to export for this year.")); return; }
    const cur = scopeMad ? "DH" : "GBP";
    const c = (v) => (scopeMad ? Math.round(toMad(v)) : Number(v || 0));
    const rows = [
      [`P&L Monthly Breakdown — ${activeUnitLabel} — ${monthly.year}`],
      ["Currency", cur, `£1 = ${rates.gbp_to_mad} DH`],
      [],
      ["Month", `Gross Revenue (${cur})`, `Net Operating Costs (${cur})`, `Luxury Costs (${cur})`, `Unclassified (${cur})`, `Net Profit (${cur})`,
        ...(isProp ? ["Nights Booked", "Nights Available", "Occupancy %", `ADR (${cur})`] : [])],
      ...monthly.months.map((m, i) => [MONTHS[i], c(m.gross), c(m.operating), c(m.luxury), c(m.unclassified), c(m.net),
        ...(isProp ? [m.nights || 0, m.available_nights, m.occupancy, c(m.adr)] : [])]),
      ["TOTAL", c(monthly.totals.gross), c(monthly.totals.operating), c(monthly.totals.luxury),
        c(monthly.totals.unclassified), c(monthly.totals.net),
        ...(isProp ? [monthly.totals.nights, monthly.totals.available_nights, monthly.totals.occupancy, c(monthly.totals.adr)] : [])],
    ];
    downloadCsv(`pnl-monthly-${monthly.year}.csv`, rows, monthly.months.length);
  };

  // ---- mutations ----
  const addRevenue = async () => {
    const r = await client.post("/pnl/revenue", { name: "", source: "not_set", closer: "", amount: 0, date: iso(new Date()), unit_id: defaultUnitId });
    setData((d) => ({ ...d, revenue: [r.data, ...d.revenue] }));
  };
  const patchRevenue = async (id, patch) => {
    setData((d) => ({ ...d, revenue: d.revenue.map((r) => (r.id === id ? { ...r, ...patch } : r)) }));
    try {
      // The saved row is the source of truth: the server re-derives the currency
      // columns the user didn't type in, so echo it back into state.
      const res = await client.put(`/pnl/revenue/${id}`, patch);
      setData((d) => ({ ...d, revenue: d.revenue.map((r) => (r.id === id ? res.data : r)) }));
    } catch (e) { toast.error(tr("Save failed")); load(); }
    if (patch.amount != null || patch.date || patch.unit_id || patch.check_in || patch.check_out) loadMonthly();
  };
  const addExpense = async () => {
    const defCat = unitKind(defaultUnitId) === "property" ? "Electricity" : "Software Costs";
    const r = await client.post("/pnl/expense", { category: defCat, description: "", amount: 0, date: iso(new Date()), unit_id: defaultUnitId });
    setData((d) => ({ ...d, expenses: [r.data, ...d.expenses] }));
  };
  const patchExpense = async (id, patch) => {
    setData((d) => ({ ...d, expenses: d.expenses.map((e) => (e.id === id ? { ...e, ...patch } : e)) }));
    try {
      const res = await client.put(`/pnl/expense/${id}`, patch);
      setData((d) => ({ ...d, expenses: d.expenses.map((e) => (e.id === id ? res.data : e)) }));
    } catch (e) { toast.error(tr("Save failed")); load(); }
    if (patch.amount != null || patch.date || patch.unit_id || patch.cost_type) loadMonthly();
  };
  // Picking a category re-suggests the cost type; you can still override it after.
  const setExpenseCategory = (row, category) => {
    const suggested = (data?.category_cost_type || {})[category] || "unclassified";
    patchExpense(row.id, { category, cost_type: suggested });
  };

  // 5-second undo delete: remove from view, actually delete after 5s unless undone.
  const softDelete = (kind, row) => {
    const key = kind + row.id;
    setData((d) => ({ ...d, [kind]: d[kind].filter((x) => x.id !== row.id) }));
    const timer = setTimeout(async () => {
      delete pendingDeletes.current[key];
      try { await client.delete(`/pnl/${kind === "revenue" ? "revenue" : "expense"}/${row.id}`); loadMonthly(); } catch (e) { load(); }
    }, 5000);
    pendingDeletes.current[key] = { row, timer };
    toast(`${kind === "revenue" ? "Revenue row" : "Expense"} deleted`, {
      duration: 5000,
      position: "bottom-right",
      action: {
        label: "Undo",
        onClick: () => {
          const p = pendingDeletes.current[key];
          if (!p) return;
          clearTimeout(p.timer);
          delete pendingDeletes.current[key];
          setData((d) => {
            const list = [...d[kind], p.row].sort((a, b) => (b.date || "").localeCompare(a.date || ""));
            return { ...d, [kind]: list };
          });
        },
      },
    });
  };
  const askDelete = (kind, row) => setConfirmDel({ kind, row });
  const doConfirmDelete = () => {
    if (!confirmDel) return;
    softDelete(confirmDel.kind, confirmDel.row);
    setConfirmDel(null);
  };

  const setPreset = (key) => {
    const y = now.getFullYear(), m = now.getMonth();
    let start, end = iso(now), label;
    if (key === "month") { start = iso(new Date(y, m, 1)); end = iso(new Date(y, m + 1, 0)); label = `${tr("This month")} (${tr(MONTHS[m])} ${y})`; }
    else if (key === "7") { const s = new Date(now); s.setDate(s.getDate() - 7); start = iso(s); label = "Last 7 days"; }
    else if (key === "30") { const s = new Date(now); s.setDate(s.getDate() - 30); start = iso(s); label = "Last 30 days"; }
    else if (key === "12") { const s = new Date(now); s.setMonth(s.getMonth() - 12); start = iso(s); label = "Last 12 months"; }
    else { start = null; end = null; label = "All time"; }
    setPeriod({ label, start, end });
  };
  const setMonth = (mi) => {
    const y = now.getFullYear();
    setPeriod({ label: `${tr(MONTHS[mi])} ${y}`, start: iso(new Date(y, mi, 1)), end: iso(new Date(y, mi + 1, 0)) });
  };

  const sources = data?.sources || Object.keys(SOURCE_LABELS);
  const categories = data?.categories || ["Software Costs", "Other"];
  const closers = data?.closers || [];

  const NoteCell = ({ note, onSave }) => {
    const val = useRef(note || "");
    useEffect(() => { val.current = note || ""; }, [note]);
    const save = () => { if (val.current !== (note || "")) onSave(val.current); };
    return (
      <Popover onOpenChange={(o) => { if (!o) save(); }}>
        <PopoverTrigger asChild>
          <button data-testid="note-btn" className={`grid h-8 w-8 place-items-center rounded-lg border ${note ? "border-brand-green/40 bg-brand-green/10 text-brand-green" : "border-white/10 bg-white/[0.03] text-muted-foreground hover:text-white"}`}>
            <StickyNote className="h-4 w-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-72 glass-strong border-white/10">
          <p className="mb-2 text-[10px] uppercase tracking-widest text-muted-foreground">{tr("Note")}</p>
          <Textarea data-testid="note-input" defaultValue={note || ""} placeholder={tr("Add a note…")} onChange={(e) => { val.current = e.target.value; }} onBlur={save} className="min-h-[90px] border-white/8 bg-input/40 text-sm" />
        </PopoverContent>
      </Popover>
    );
  };

  // Read-only derived money cell (used for £/€ on dirham-led property rows).
  const roCell = (text, testId) => (
    <td className="pr-3"><span className="text-xs text-muted-foreground" data-testid={testId}>{text}</span></td>
  );
  const madRevCell = (r) => (
    <td className="pr-3">
      <AmountInput value={Math.round(rowMad(r))} prefix="DH" testId="revenue-mad" className="h-9 w-[130px] border-brand-green/25 bg-input/40"
        onCommit={(v) => patchRevenue(r.id, { amountMad: v, amount: Number(fromMad(v).toFixed(2)) })} />
    </td>
  );

  const KpiCard = ({ icon: Icon, label, value, sub, tone }) => {
    const box = tone === "red" ? "border-brand-red/25 bg-brand-red/[0.04]"
      : tone === "green" ? "border-brand-green/25 bg-brand-green/[0.04]"
      : tone === "amber" ? "border-amber-400/25 bg-amber-400/[0.04]" : "border-white/[0.06] bg-card/40";
    const chip = tone === "red" ? "bg-brand-red/15 text-brand-red"
      : tone === "green" ? "bg-brand-green/15 text-brand-green"
      : tone === "amber" ? "bg-amber-400/15 text-amber-300" : "bg-white/5 text-white";
    const text = tone === "red" ? "text-brand-red" : tone === "green" ? "text-brand-green"
      : tone === "amber" ? "text-amber-300" : "text-white";
    return (
      <div className={`rounded-2xl border p-5 ${box}`}>
        <div className="flex items-center gap-2 text-muted-foreground">
          <span className={`grid h-8 w-8 place-items-center rounded-lg ${chip}`}><Icon className="h-4 w-4" /></span>
          <span className="text-[11px] uppercase tracking-wide">{tr(label)}</span>
        </div>
        <p className={`mt-3 font-display text-3xl font-semibold ${text}`}>{value}</p>
        <p className="mt-1 text-xs text-muted-foreground">{sub}</p>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 flex flex-wrap items-center gap-3 border-b border-white/[0.06] bg-background/80 px-4 py-3 backdrop-blur-xl sm:px-6">
        <button onClick={back} className="inline-flex items-center gap-1 rounded-lg border border-white/12 bg-white/[0.03] px-3 py-2 text-sm text-white hover:bg-white/[0.07]" data-testid="pnl-back-btn"><ArrowLeft className="h-4 w-4" /> {tr("Console")}</button>
        <span className="grid h-9 w-9 place-items-center rounded-xl bg-brand-green/15 text-brand-green"><Wallet className="h-5 w-5" /></span>
        <div className="min-w-0">
          <h1 className="font-display text-lg font-semibold text-white">P&amp;L</h1>
          <p className="truncate text-xs text-muted-foreground">{activeUnitLabel} · {period.label} · {(data?.revenue || []).length} {tr("revenue rows")}, {expenses.length} {tr("expenses")}</p>
        </div>

        <div className="ml-auto flex flex-wrap items-end justify-end gap-2 xl:gap-4">
          {/* Business scope: group total or a single venture */}
          <div className="shrink-0">
            <p className="mb-1 text-[10px] uppercase tracking-widest text-muted-foreground">{tr("Business")}</p>
            <div className="flex items-center gap-1.5">
              <Select value={unit} onValueChange={setUnit}>
                <SelectTrigger className="h-[38px] w-[168px] border-white/12 bg-white/[0.03] text-sm lg:w-[210px]" data-testid="pnl-unit-select">
                  <span className="flex items-center gap-2 truncate"><Building2 className="h-4 w-4 shrink-0 text-brand-green" /><SelectValue /></span>
                </SelectTrigger>
                <SelectContent className="glass-strong border-white/10">
                  <SelectItem value={ALL_UNITS}>{tr("All Businesses (group)")}</SelectItem>
                  {units.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <button onClick={() => setManageOpen(true)} data-testid="manage-units-btn" title={tr("Manage businesses")}
                className="grid h-[38px] w-[38px] place-items-center rounded-lg border border-white/12 bg-white/[0.03] text-muted-foreground hover:text-white">
                <Settings2 className="h-4 w-4" />
              </button>
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={exportNetCsv} data-testid="export-summary-csv-btn"
            className="h-[38px] shrink-0 border-white/12 bg-white/[0.03] text-white hover:bg-white/[0.07]"><Download className="h-4 w-4" /> <span className="hidden lg:inline">{tr("Export summary")}</span></Button>
          {/* Editable currency rates for the selected period */}
          <Popover>
            <PopoverTrigger asChild>
              <button className="flex h-[38px] shrink-0 items-center gap-2 whitespace-nowrap rounded-lg border border-white/12 bg-white/[0.03] px-3 text-sm text-white hover:bg-white/[0.07]" data-testid="pnl-rates-btn">
                <Coins className="h-4 w-4 shrink-0 text-brand-green" /> £1 = {rates.gbp_to_mad} DH <span className="hidden xl:inline">· €1 = {rates.eur_to_mad} DH</span>
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 glass-strong border-white/10">
              <p className="mb-1 text-[10px] uppercase tracking-widest text-muted-foreground">Exchange rates ({rates.month || "this month"})</p>
              <p className="mb-3 text-xs text-muted-foreground">{tr("Rates apply to the currently selected period.")}</p>
              <label className="mb-1 block text-xs text-muted-foreground">£1 equals (dirhams)</label>
              <Input data-testid="rate-gbp" type="number" step="0.01" defaultValue={rates.gbp_to_mad}
                onBlur={(e) => { const v = Number(e.target.value); if (v && v !== rates.gbp_to_mad) saveRates({ gbp_to_mad: v }); }}
                className="mb-3 h-9 border-white/8 bg-input/40" />
              <label className="mb-1 block text-xs text-muted-foreground">€1 equals (dirhams)</label>
              <Input data-testid="rate-eur" type="number" step="0.01" defaultValue={rates.eur_to_mad}
                onBlur={(e) => { const v = Number(e.target.value); if (v && v !== rates.eur_to_mad) saveRates({ eur_to_mad: v }); }}
                className="h-9 border-white/8 bg-input/40" />
            </PopoverContent>
          </Popover>

          <div className="shrink-0">
            <p className="mb-1 text-[10px] uppercase tracking-widest text-muted-foreground">{tr("Period")}</p>
            <Popover>
              <PopoverTrigger asChild>
                <button className="flex h-[38px] items-center gap-2 whitespace-nowrap rounded-lg border border-white/12 bg-white/[0.03] px-3 text-sm text-white hover:bg-white/[0.07]" data-testid="pnl-period-btn"><Calendar className="h-4 w-4 shrink-0 text-muted-foreground" /> <span className="max-w-[170px] truncate">{period.label}</span></button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-72 glass-strong border-white/10">
                <p className="mb-2 text-[10px] uppercase tracking-widest text-muted-foreground">{tr("Presets")}</p>
                <div className="flex flex-wrap gap-1.5">
                  {[["month", "This month"], ["7", "Last 7 days"], ["30", "Last 30 days"], ["12", "Last 12 months"], ["all", "All time"]].map(([k, l]) => (
                    <button key={k} onClick={() => setPreset(k)} data-testid={`preset-${k}`} className="rounded-lg border border-white/12 bg-white/[0.03] px-2.5 py-1 text-xs text-white hover:bg-brand-green/15 hover:border-brand-green/30">{tr(l)}</button>
                  ))}
                </div>
                <p className="mb-2 mt-4 text-[10px] uppercase tracking-widest text-muted-foreground">{tr("Months of")} {now.getFullYear()}</p>
                <div className="grid grid-cols-4 gap-1.5">
                  {MONTHS.map((m, i) => (
                    <button key={m} onClick={() => setMonth(i)} className="rounded-lg border border-white/12 bg-white/[0.03] px-2 py-1.5 text-xs text-white hover:bg-brand-green/15 hover:border-brand-green/30">{m}</button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6">
        <div className="mb-5 inline-flex rounded-lg border border-white/10 bg-white/[0.02] p-1">
          {TABS.map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`pnl-tab-${k}`} className={`rounded-md px-4 py-1.5 text-sm font-medium ${tab === k ? "bg-brand-green/15 text-brand-green" : "text-muted-foreground hover:text-white"}`}>{tr(l)}</button>
          ))}
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="pnl-kpis">
          <KpiCard icon={TrendingUp} label={tr("Gross Revenue")} value={money(totalRev)} sub={scopeMad ? `${gbp(totalRev)} · ${eur(totalRevEur)}` : `${mad(totalRevMad)} · ${eur(totalRevEur)}`} />
          <KpiCard icon={TrendingDown} label={tr("Net Operating Costs")} value={money(costBuckets.operating)} sub={tr("Needed to run the business")} tone="red" />
          <KpiCard icon={Gem} label={tr("Luxury Costs")} value={money(costBuckets.luxury)} sub={tr("Discretionary spend")} tone="amber" />
          <KpiCard icon={DollarSign} label={tr("Net Profit")} value={money(net)} sub={net >= 0 ? tr("In the green") : tr("In the red")} tone={net >= 0 ? "green" : "red"} />
        </div>

        {/* The equation, spelled out for the selected period */}
        <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-2xl border border-white/[0.06] bg-card/40 px-5 py-4 text-sm" data-testid="pnl-equation">
          <span className="text-white">{money(totalRev)} <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{tr("gross")}</span></span>
          <span className="text-muted-foreground">−</span>
          <span className="text-brand-red">{money(costBuckets.operating)} <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{tr("operating")}</span></span>
          <span className="text-muted-foreground">−</span>
          <span className="text-amber-300">{money(costBuckets.luxury)} <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{tr("luxury")}</span></span>
          {costBuckets.unclassified > 0 && (<>
            <span className="text-muted-foreground">−</span>
            <span className="text-muted-foreground">{money(costBuckets.unclassified)} <span className="text-[10px] uppercase tracking-widest">{tr("unclassified")}</span></span>
          </>)}
          <span className="text-muted-foreground">=</span>
          <span className={`font-display text-base font-semibold ${net >= 0 ? "text-brand-green" : "text-brand-red"}`} data-testid="equation-net">{money(net)} {tr("net profit")}</span>
        </div>

        {unclassifiedCount > 0 && (
          <div className="mt-3 flex items-center gap-3 rounded-2xl border border-amber-400/25 bg-amber-400/[0.06] px-5 py-3 text-sm text-amber-200" data-testid="unclassified-banner">
            <HelpCircle className="h-4 w-4 shrink-0" />
            <span><strong>{unclassifiedCount}</strong> expense{unclassifiedCount === 1 ? "" : "s"} ({money(costBuckets.unclassified)}) still need marking as operating or luxury.</span>
          </div>
        )}

        {/* Month-by-month gross → operating → luxury → net */}
        <div className="mt-6 rounded-2xl border border-white/[0.06] bg-card/40 p-5 sm:p-6" data-testid="pnl-monthly">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h2 className="flex flex-wrap items-center gap-2 font-display text-lg font-semibold text-white"><BarChart3 className="h-4 w-4 text-brand-green" /> {tr("Monthly breakdown")} — {activeUnitLabel} <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{scopeMad ? tr("in dirhams") : tr("in pounds")}</span>
              {isProp && (
                <span className="rounded-lg border border-brand-green/25 bg-brand-green/[0.06] px-2.5 py-1 text-xs font-normal text-brand-green" data-testid="year-occupancy-chip">
                  <BedDouble className="mr-1 inline h-3.5 w-3.5" />{monthly?.totals?.nights || 0} nights · {monthly?.totals?.occupancy || 0}% occupancy · ADR {money(monthly?.totals?.adr)}
                </span>
              )}
            </h2>
            <div className="flex items-center gap-2">
              <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
                <SelectTrigger className="h-9 w-[110px] border-white/8 bg-input/40 text-sm" data-testid="monthly-year-select"><SelectValue /></SelectTrigger>
                <SelectContent className="glass-strong border-white/10">
                  {[0, 1, 2, 3].map((o) => { const y = new Date().getFullYear() - o; return <SelectItem key={y} value={String(y)}>{y}</SelectItem>; })}
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" onClick={exportMonthlyCsv} data-testid="export-monthly-csv-btn" className="border-white/12 bg-white/[0.03] text-white hover:bg-white/[0.07]"><Download className="h-4 w-4" /> {tr("Export CSV")}</Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className={`w-full text-sm ${isProp ? "min-w-[1040px]" : "min-w-[720px]"}`}>
              <thead><tr className="border-b border-white/8 text-[10px] uppercase tracking-widest text-muted-foreground">
                <th className="py-2 text-left font-medium">{tr("Month")}</th><th className="text-right font-medium">{tr("Gross")}</th>
                <th className="text-right font-medium">{tr("Operating")}</th><th className="text-right font-medium">{tr("Luxury")}</th>
                <th className="text-right font-medium">{tr("Unclassified")}</th><th className="text-right font-medium">{tr("Net Profit")}</th>
                {isProp && <>
                  <th className="text-right font-medium">{tr("Nights")}</th>
                  <th className="text-right font-medium">{tr("Occupancy")}</th>
                  <th className="text-right font-medium">{tr("ADR")}</th>
                </>}</tr></thead>
              <tbody>
                {(monthly?.months || []).map((m, i) => {
                  const empty = !m.gross && !m.operating && !m.luxury && !m.unclassified;
                  return (
                    <tr key={m.month} className={`border-b border-white/[0.04] ${empty ? "text-muted-foreground/60" : "text-white"}`} data-testid="monthly-row">
                      <td className="py-2 text-left">{tr(MONTHS[i])}</td>
                      <td className="text-right">{money(m.gross)}</td>
                      <td className="text-right text-brand-red">{m.operating ? money(m.operating) : "—"}</td>
                      <td className="text-right text-amber-300">{m.luxury ? money(m.luxury) : "—"}</td>
                      <td className="text-right">{m.unclassified ? money(m.unclassified) : "—"}</td>
                      <td className={`text-right font-medium ${empty ? "" : m.net >= 0 ? "text-brand-green" : "text-brand-red"}`}>{money(m.net)}</td>
                      {isProp && <>
                        <td className="py-1 pl-3 text-right">
                          {m.nights_source === "bookings" ? (
                            <>
                              <span className="text-white" data-testid={`nights-${MONTHS[i].toLowerCase()}`}>{m.nights}</span>
                              <p className="mt-0.5 text-[10px] text-brand-green">{tr("from bookings")}</p>
                            </>
                          ) : (
                            <>
                              <div className="flex justify-end">
                                <AmountInput value={m.nights || 0} onCommit={(v) => saveNights(m.month, v)} testId={`nights-${MONTHS[i].toLowerCase()}`}
                                  className="h-8 w-[72px] border-white/8 bg-input/40 text-right text-xs" />
                              </div>
                              <p className="mt-0.5 text-[10px] text-muted-foreground">of {m.available_nights}</p>
                            </>
                          )}
                        </td>
                        <td className={`text-right ${m.occupancy >= 60 ? "text-brand-green" : m.occupancy > 0 ? "text-amber-300" : "text-muted-foreground/60"}`} data-testid="monthly-occupancy">{m.nights ? `${m.occupancy}%` : "—"}</td>
                        <td className="text-right" data-testid="monthly-adr">{m.adr ? money(m.adr) : "—"}</td>
                      </>}
                    </tr>
                  );
                })}
              </tbody>
              <tfoot><tr className="text-[11px]" data-testid="monthly-total-row">
                <td className="pt-3 text-left uppercase tracking-widest text-muted-foreground">{tr("Year total")}</td>
                <td className="pt-3 text-right font-display font-semibold text-white">{money(monthly?.totals?.gross)}</td>
                <td className="pt-3 text-right font-display font-semibold text-brand-red">{money(monthly?.totals?.operating)}</td>
                <td className="pt-3 text-right font-display font-semibold text-amber-300">{money(monthly?.totals?.luxury)}</td>
                <td className="pt-3 text-right font-display font-semibold text-muted-foreground">{money(monthly?.totals?.unclassified)}</td>
                <td className={`pt-3 text-right font-display font-semibold ${(monthly?.totals?.net || 0) >= 0 ? "text-brand-green" : "text-brand-red"}`}>{money(monthly?.totals?.net)}</td>
                {isProp && <>
                  <td className="pt-3 text-right font-display font-semibold text-white">{monthly?.totals?.nights || 0}</td>
                  <td className="pt-3 text-right font-display font-semibold text-brand-green" data-testid="year-occupancy">{monthly?.totals?.occupancy || 0}%</td>
                  <td className="pt-3 text-right font-display font-semibold text-white" data-testid="year-adr">{money(monthly?.totals?.adr)}</td>
                </>}
              </tr></tfoot>
            </table>
          </div>
        </div>

        {/* Revenue */}
        <div className="mt-6 rounded-2xl border border-white/[0.06] bg-card/40 p-5 sm:p-6" data-testid="pnl-revenue">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="flex items-center gap-2 font-display text-lg font-semibold text-white"><GraduationCap className="h-4 w-4 text-brand-green" /> {tr("Revenue")}</h2>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={exportCsv} data-testid="export-csv-btn" className="border-white/12 bg-white/[0.03] text-white hover:bg-white/[0.07]"><Download className="h-4 w-4" /> {tr("Export CSV")}</Button>
              <Button size="sm" onClick={addRevenue} className="bg-brand-green hover:bg-brand-green/90 text-white" data-testid="add-revenue-btn"><Plus className="h-4 w-4" /> {tr("Revenue Add")}</Button>
            </div>
          </div>
          <div className="max-h-[62vh] overflow-auto">
            <table className={`w-full text-sm ${propScope ? "min-w-[1180px]" : "min-w-[1280px]"}`}>
              <thead><tr className="text-[10px] uppercase tracking-widest text-muted-foreground [&>th]:sticky [&>th]:top-0 [&>th]:z-10 [&>th]:bg-card [&>th]:border-b [&>th]:border-white/8 [&>th]:py-2">
                {propScope ? (<>
                  <th className="py-2 pr-3 text-left font-medium">{tr("Check-in")}</th><th className="pr-3 text-left font-medium">{tr("Check-out")}</th>
                  <th className="pr-4 text-left font-medium">{tr("Nights")}</th><th className="pr-3 text-left font-medium">{tr("Booking / Guest")}</th>
                  <th className="pr-3 text-left font-medium">{tr("Business")}</th><th className="pr-3 text-left font-medium">{tr("Source")}</th>
                </>) : (<>
                  <th className="py-2 text-left font-medium">{tr("Date joined")}</th><th className="text-left font-medium">{tr("Revenue Name")}</th>
                  <th className="text-left font-medium">{tr("Business")}</th>
                  <th className="text-left font-medium">{tr("Source")}</th><th className="text-left font-medium">{tr("Closer")}</th>
                </>)}
                {scopeMad
                  ? <><th className="text-left font-medium">{tr("Amount (DH)")}</th><th className="text-left font-medium">{tr("GBP")}</th><th className="text-left font-medium">{tr("EUR")}</th></>
                  : <><th className="text-left font-medium">{tr("Amount (£)")}</th><th className="text-left font-medium">{tr("MAD")}</th><th className="text-left font-medium">{tr("EUR")}</th></>}
                <th className="text-left font-medium">{tr("Note")}</th><th></th></tr></thead>
              <tbody>
                {revenue.map((r) => (
                  <tr key={r.id} className="border-b border-white/[0.04]" data-testid="revenue-row">
                    {propScope ? (<>
                      <td className="py-2 pr-3">
                        <Input type="date" value={r.check_in || ""} data-testid="revenue-check-in"
                          onChange={(e) => patchRevenue(r.id, { check_in: e.target.value, date: e.target.value || r.date })}
                          className="h-9 w-[140px] border-white/8 bg-input/40 text-xs" />
                      </td>
                      <td className="pr-3">
                        <Input type="date" value={r.check_out || ""} data-testid="revenue-check-out" min={r.check_in || undefined}
                          onChange={(e) => patchRevenue(r.id, { check_out: e.target.value })}
                          className="h-9 w-[140px] border-white/8 bg-input/40 text-xs" />
                      </td>
                      <td className="pr-4">
                        <span data-testid="revenue-nights" className={`inline-flex h-9 min-w-[52px] items-center justify-center rounded-lg border px-3 text-sm ${stayNights(r) ? "border-brand-green/30 bg-brand-green/10 text-brand-green" : "border-white/[0.08] bg-white/[0.02] text-muted-foreground"}`}>
                          {stayNights(r) || "—"}
                        </span>
                      </td>
                      <td className="pr-3"><Input value={r.name} onChange={(e) => patchRevenue(r.id, { name: e.target.value })} placeholder={tr("Guest or booking ref")} className="h-9 min-w-[220px] border-white/8 bg-input/40" /></td>
                    </>) : (<>
                      <td className="py-2 pr-3">
                        <Input type="date" value={r.date || ""} onChange={(e) => patchRevenue(r.id, { date: e.target.value })} className="h-9 w-[140px] border-white/8 bg-input/40 text-xs" />
                      </td>
                      <td className="pr-3"><Input value={r.name} onChange={(e) => patchRevenue(r.id, { name: e.target.value })} placeholder={tr("Revenue name")} className="h-9 min-w-[280px] border-white/8 bg-input/40" /></td>
                    </>)}
                    {unit !== ALL_UNITS ? lockedUnitCell(r, "revenue-unit-locked") : (
                      <td className="pr-3">
                        <Select value={r.unit_id || (data?.main_unit_id || MAIN_ID)} onValueChange={(v) => patchRevenue(r.id, { unit_id: v })}>
                          <SelectTrigger className="h-9 w-[180px] border-white/8 bg-input/40" data-testid="revenue-unit"><SelectValue /></SelectTrigger>
                          <SelectContent className="glass-strong border-white/10">
                            {units.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                    )}
                    <td className="pr-3">
                      <Select value={r.source} onValueChange={(v) => patchRevenue(r.id, { source: v })}>
                        <SelectTrigger className="h-9 w-[240px] border-white/8 bg-input/40" data-testid="revenue-source"><SelectValue /></SelectTrigger>
                        <SelectContent className="glass-strong border-white/10">
                          {sourcesFor(r).map((s) => <SelectItem key={s} value={s}>{tr(SOURCE_LABELS[s] || s)}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    {/* Short lets have no closer — only the mentorship side does. */}
                    {!propScope && (
                      <td className="pr-3">
                        {unitKind(r.unit_id) === "property" ? <span className="text-xs text-muted-foreground">—</span> : (
                          <Select value={r.closer || "__none"} onValueChange={(v) => patchRevenue(r.id, { closer: v === "__none" ? "" : v })}>
                            <SelectTrigger className="h-9 w-[160px] border-white/8 bg-input/40"><SelectValue placeholder={tr("Closer")} /></SelectTrigger>
                            <SelectContent className="glass-strong border-white/10">
                              <SelectItem value="__none">{tr("— None —")}</SelectItem>
                              {closers.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        )}
                      </td>
                    )}
                    {isMadRow(r.unit_id) ? (
                      // Property lets: dirhams are the master figure, £/€ derive from the period rates.
                      scopeMad
                        ? <>{madRevCell(r)}{roCell(gbp(r.amount), "revenue-gbp-derived")}{roCell(eur(rowEur(r)))}</>
                        : <>{roCell(gbp(r.amount), "revenue-gbp-derived")}{madRevCell(r)}{roCell(eur(rowEur(r)))}</>
                    ) : (<>
                      <td className="pr-3">
                        <AmountInput value={r.amount} onCommit={(v) => patchRevenue(r.id, { amount: v })} prefix="£" testId="revenue-amount" className="h-9 w-[120px] border-white/8 bg-input/40" />
                      </td>
                      <td className="pr-3">
                        <AmountInput value={Math.round(rowMad(r))} prefix="DH" testId="revenue-mad" className="h-9 w-[120px] border-white/8 bg-input/40"
                          onCommit={(v) => patchRevenue(r.id, { amountMad: v, amount: Number(fromMad(v).toFixed(2)) })} />
                      </td>
                      <td className="pr-3">
                        <AmountInput value={Math.round(rowEur(r) * 100) / 100} prefix="€" testId="revenue-eur" className="h-9 w-[120px] border-white/8 bg-input/40"
                          onCommit={(v) => patchRevenue(r.id, { amountEur: v, amount: Number(fromMad(Number(v || 0) * Number(rates.eur_to_mad || 0)).toFixed(2)) })} />
                      </td>
                    </>)}
                    <td className="pr-3">
                      <button data-testid="note-btn" onClick={() => setNotesRow(r)} className="grid h-8 w-8 place-items-center rounded-lg border border-white/10 bg-white/[0.03] text-muted-foreground hover:text-white"><StickyNote className="h-4 w-4" /></button>
                    </td>
                    <td><button onClick={() => askDelete("revenue", r)} className="text-muted-foreground hover:text-brand-red" data-testid="del-revenue"><Trash2 className="h-4 w-4" /></button></td>
                  </tr>
                ))}
                {revenue.length === 0 && <tr><td colSpan={propScope ? 11 : 10} className="py-6 text-center text-sm text-muted-foreground">{tr("No revenue in this period.")}</td></tr>}
              </tbody>
              <tfoot><tr>
                <td colSpan={propScope ? 6 : 5} className="pt-3 text-[10px] uppercase tracking-widest text-muted-foreground">Total{propScope && revNights ? ` · ${revNights} nights` : ""}</td>
                {scopeMad ? (<>
                  <td className="pt-3 font-display font-semibold text-brand-green">{mad(totalRevMad)}</td>
                  <td className="pt-3 font-display font-semibold text-brand-green">{gbp(totalRev)}</td>
                  <td className="pt-3 font-display font-semibold text-brand-green">{eur(totalRevEur)}</td>
                </>) : (<>
                  <td className="pt-3 font-display font-semibold text-brand-green">{gbp(totalRev)}</td>
                  <td className="pt-3 font-display font-semibold text-brand-green">{mad(totalRevMad)}</td>
                  <td className="pt-3 font-display font-semibold text-brand-green">{eur(totalRevEur)}</td>
                </>)}
                <td colSpan={2}></td>
              </tr></tfoot>
            </table>
          </div>
        </div>

        {/* Expenses */}
        <div className="mt-6 rounded-2xl border border-white/[0.06] bg-card/40 p-5 sm:p-6" data-testid="pnl-expenses">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="flex items-center gap-2 font-display text-lg font-semibold text-white"><Receipt className="h-4 w-4 text-brand-green" /> {tr("Expenses")}</h2>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={exportExpensesCsv} data-testid="export-expenses-csv-btn" className="border-white/12 bg-white/[0.03] text-white hover:bg-white/[0.07]"><Download className="h-4 w-4" /> {tr("Export CSV")}</Button>
              <Button size="sm" onClick={addExpense} className="bg-brand-green hover:bg-brand-green/90 text-white" data-testid="add-expense-btn"><Plus className="h-4 w-4" /> {tr("Add expense")}</Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1080px] text-sm">
              <thead><tr className="border-b border-white/8 text-[10px] uppercase tracking-widest text-muted-foreground">
                <th className="py-2 text-left font-medium">{tr("Date")}</th><th className="text-left font-medium">{tr("Business")}</th>
                <th className="text-left font-medium">{tr("Category")}</th><th className="text-left font-medium">{tr("Operating / Luxury")}</th>
                <th className="text-left font-medium">{tr("Description")}</th><th className="text-left font-medium">Amount ({scopeMad ? "DH" : "£"})</th>
                <th className="text-left font-medium">{tr("Note")}</th><th></th></tr></thead>
              <tbody>
                {expenses.map((e) => {
                  const ct = COST_TYPES.includes(e.cost_type) ? e.cost_type : "unclassified";
                  return (
                  <tr key={e.id} className={`border-b border-white/[0.04] ${ct === "unclassified" ? "bg-amber-400/[0.03]" : ""}`} data-testid="expense-row">
                    <td className="py-2 pr-3"><Input type="date" value={e.date || ""} onChange={(ev) => patchExpense(e.id, { date: ev.target.value })} className="h-9 w-[140px] border-white/8 bg-input/40 text-xs" /></td>
                    {unit !== ALL_UNITS ? lockedUnitCell(e, "expense-unit-locked") : (
                      <td className="pr-3">
                        <Select value={e.unit_id || (data?.main_unit_id || MAIN_ID)} onValueChange={(v) => patchExpense(e.id, { unit_id: v })}>
                          <SelectTrigger className="h-9 w-[180px] border-white/8 bg-input/40" data-testid="expense-unit"><SelectValue /></SelectTrigger>
                          <SelectContent className="glass-strong border-white/10">
                            {units.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                    )}
                    <td className="pr-3">
                      <Select value={e.category} onValueChange={(v) => setExpenseCategory(e, v)}>
                        <SelectTrigger className="h-9 w-[190px] border-white/8 bg-input/40" data-testid="expense-category"><SelectValue /></SelectTrigger>
                        <SelectContent className="glass-strong border-white/10">
                          {catsFor(e).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="pr-3">
                      <Select value={ct} onValueChange={(v) => patchExpense(e.id, { cost_type: v })}>
                        <SelectTrigger className={`h-9 w-[150px] border ${COST_TYPE_STYLES[ct]}`} data-testid="expense-cost-type"><SelectValue /></SelectTrigger>
                        <SelectContent className="glass-strong border-white/10">
                          {COST_TYPES.map((t) => <SelectItem key={t} value={t}>{tr(COST_TYPE_LABELS[t])}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="pr-3"><Input value={e.description} onChange={(ev) => patchExpense(e.id, { description: ev.target.value })} placeholder={tr("Description")} className="h-9 min-w-[180px] border-white/8 bg-input/40" /></td>
                    <td className="pr-3">{isMadRow(e.unit_id) ? (
                      <div>
                        <AmountInput value={Math.round(e.amountMad != null ? e.amountMad : toMad(e.amount))} prefix="DH" testId="expense-amount"
                          className="h-9 w-[130px] border-brand-green/25 bg-input/40"
                          onCommit={(v) => patchExpense(e.id, { amountMad: v, amount: Number(fromMad(v).toFixed(2)) })} />
                        <p className="mt-0.5 text-[10px] text-muted-foreground" data-testid="expense-gbp-derived">≈ {gbp(e.amount)}</p>
                      </div>
                    ) : (
                      <AmountInput value={e.amount} onCommit={(v) => patchExpense(e.id, { amount: v })} prefix="£" testId="expense-amount" className="h-9 w-[120px] border-white/8 bg-input/40" />
                    )}</td>
                    <td className="pr-3"><NoteCell note={e.note} onSave={(v) => patchExpense(e.id, { note: v })} /></td>
                    <td><button onClick={() => askDelete("expenses", e)} className="text-muted-foreground hover:text-brand-red" data-testid="del-expense"><Trash2 className="h-4 w-4" /></button></td>
                  </tr>
                  );
                })}
                {expenses.length === 0 && <tr><td colSpan={8} className="py-6 text-center text-sm text-muted-foreground">{tr("No expenses in this period.")}</td></tr>}
              </tbody>
              <tfoot>
                <tr><td colSpan={5} className="pt-3 text-[10px] uppercase tracking-widest text-muted-foreground">{tr("Total expenses")}</td><td className="pt-3 font-display font-semibold text-brand-red">{money(totalExp)}</td><td colSpan={2}></td></tr>
                <tr><td colSpan={5} className="pt-1 text-[10px] uppercase tracking-widest text-muted-foreground">{tr("of which operating")}</td><td className="pt-1 text-xs text-brand-red">{money(costBuckets.operating)}</td><td colSpan={2}></td></tr>
                <tr><td colSpan={5} className="pt-1 text-[10px] uppercase tracking-widest text-muted-foreground">{tr("of which luxury")}</td><td className="pt-1 text-xs text-amber-300">{money(costBuckets.luxury)}</td><td colSpan={2}></td></tr>
              </tfoot>
            </table>
          </div>
        </div>
      </main>

      <PnlNotesDialog open={!!notesRow} onOpenChange={(o) => { if (!o) setNotesRow(null); }} revenueId={notesRow?.id} name={notesRow?.name} />

      <Dialog open={manageOpen} onOpenChange={setManageOpen}>
        <DialogContent className="max-w-md glass-strong border-white/10">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display text-lg text-white"><Building2 className="h-5 w-5 text-brand-green" /> {tr("Businesses")}</DialogTitle>
            <DialogDescription className="text-muted-foreground">{tr("Each venture keeps its own revenue and costs, and rolls up into the group total.")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2" data-testid="units-list">
            {units.map((u) => (
              <div key={u.id} className="flex items-center gap-2 rounded-xl border border-white/[0.08] bg-white/[0.02] px-3 py-2">
                {renaming?.id === u.id ? (
                  <>
                    <Input value={renaming.name} autoFocus onChange={(ev) => setRenaming({ ...renaming, name: ev.target.value })}
                      onKeyDown={(ev) => { if (ev.key === "Enter") saveRename(); }}
                      className="h-8 border-white/8 bg-input/40 text-sm" data-testid="rename-unit-input" />
                    <button onClick={saveRename} data-testid="save-unit-name" className="text-brand-green hover:opacity-80"><Check className="h-4 w-4" /></button>
                  </>
                ) : (
                  <>
                    <span className="flex-1 truncate text-sm text-white">{u.name}</span>
                    <span className={`rounded-md border px-1.5 py-0.5 text-[10px] uppercase tracking-wide ${u.kind === "property" ? "border-brand-green/30 bg-brand-green/10 text-brand-green" : "border-white/12 bg-white/[0.03] text-muted-foreground"}`} data-testid="unit-kind-badge">
                      {u.kind === "property" ? "Property · DH" : "Business · £"}
                    </span>
                    <button onClick={() => setRenaming({ id: u.id, name: u.name })} className="text-muted-foreground hover:text-white" data-testid="rename-unit-btn"><Pencil className="h-3.5 w-3.5" /></button>
                    {u.id !== (data?.main_unit_id || "unit_main") && (
                      <button onClick={() => removeUnit(u)} className="text-muted-foreground hover:text-brand-red" data-testid="delete-unit-btn"><Trash2 className="h-3.5 w-3.5" /></button>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 pt-1">
            <Input value={newUnitName} onChange={(ev) => setNewUnitName(ev.target.value)} placeholder={tr("New business name")}
              onKeyDown={(ev) => { if (ev.key === "Enter") addUnit(); }}
              className="h-9 border-white/8 bg-input/40" data-testid="new-unit-input" />
            <Select value={newUnitKind} onValueChange={setNewUnitKind}>
              <SelectTrigger className="h-9 w-[150px] border-white/8 bg-input/40 text-xs" data-testid="new-unit-kind"><SelectValue /></SelectTrigger>
              <SelectContent className="glass-strong border-white/10">
                {Object.entries(UNIT_KIND_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{tr(l)}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={addUnit} data-testid="add-unit-btn" className="bg-brand-green hover:bg-brand-green/90 text-white"><Plus className="h-4 w-4" /> {tr("Add")}</Button>
          </div>
          <p className="text-xs text-muted-foreground">A <strong className="text-white">{tr("Property")}</strong> {tr("venture leads in dirhams and gets utility categories (electricity, internet, gas, water, staff, fixes…) plus booking channels (Airbnb, Booking.com, Direct). A")} <strong className="text-white">{tr("Business")}</strong> {tr("leads in £ with the mentorship categories.")}</p>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDel} onOpenChange={(o) => { if (!o) setConfirmDel(null); }}>
        <DialogContent className="max-w-sm glass-strong border-brand-red/30">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display text-lg text-white"><AlertTriangle className="h-5 w-5 text-brand-red" /> {confirmDel?.kind === "revenue" ? tr("Delete this revenue row?") : tr("Delete this expense?")}</DialogTitle>
            <DialogDescription className="text-muted-foreground">{confirmDel?.row?.name || confirmDel?.row?.description ? `"${confirmDel?.row?.name || confirmDel?.row?.description}" · ` : ""}{tr("You'll have 5 seconds to undo.")}</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 pt-1">
            <Button variant="outline" onClick={() => setConfirmDel(null)} className="flex-1 border-white/12 bg-white/[0.03] text-white hover:bg-white/[0.07]">{tr("Cancel")}</Button>
            <Button onClick={doConfirmDelete} data-testid="confirm-delete-btn" className="flex-1 bg-brand-red hover:bg-brand-redDark text-white"><Trash2 className="h-4 w-4" /> {tr("Delete")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
